<template>

  <div class="show-in-print">
    <h1 class="d-flex align-items-center mb-3">
        <span class="icon mr-2 icon-produkte_formulare" aria-hidden="true"></span>
        {{ $t('views.reservations.title') }}
      </h1>
  </div>

  <div class="row">

    <div class="col-12 px-0 hide-in-print">
      <h1 class="d-flex align-items-center">
        <span class="icon mr-2 icon-produkte_formulare" aria-hidden="true"></span>
        {{ $t('views.reservations.title') }}
      </h1>
    </div>

    <link-back to="/" i18nMessage="common.backToDashboard" class="mt-2 hide-in-print" />

    <loading-container :state="state">
      <div class="row hide-in-print">
        <div class="col-12">
          <tabs-ticket-type :tabs="getTabsTicketTypeOptions()" :activeTab="searchType" v-on:changed="setSearchType($event)" />
        </div>

        <div class="col-12 col-md-3">
          <control-search i18nLabel="views.reservations.search" v-model="searchTerm" v-on:keyup.enter="doNewSearch()" />
        </div>
        <div class="col-12 col-md-2">
          <control-date
            i18nLabel="views.reservations.searchFrom"
            v-model="searchFrom"
            @update:modelValue="doNewSearch()"
          />
        </div>
        <div class="col-12 col-md-2">
          <control-date
            i18nLabel="views.reservations.searchUntil"
            v-model="searchUntil"
            @update:modelValue="doNewSearch()"
          />
        </div>
        <div class="col-12 col-md-3">
          <button-search v-on:clicked="doNewSearch()" :labelHidden="true" />
        </div>

        <div class="col-12 col-md-2">
          <button-reset v-on:clicked="resetTermAndDate()" v-if="isResettable()" :labelHidden="true" class="m-0 p-0 btn-to-danger-link-filter" />
        </div>

        <div class="col-12 d-flex justify-content-end hide-in-print">
          <button-print i18nMessage="common.printActual" />
        </div>
      </div>

      <div class="row">
        <loading-container :state="searchState" class="px-3">
          <result-list-tickets
            :tickets="tickets"
            :sortOptions="sortOptions"
            :searchType="searchType"
            v-on:sortChanged="setSortBy($event)"
          />
        </loading-container>
      </div>

      <div class="row">
        <div class="col-12 d-flex justify-content-center">
          <result-paging :active="activePage()" :max="maxPage()" v-if="maxPage() > 1" v-on:changed="setActivePage($event)" />
        </div>
      </div>
    </loading-container>
  </div>
</template>

<script lang="ts">
import ControlDate from '@/components/controls/ControlDate.vue';
import ControlSearch from '@/components/controls/ControlSearch.vue';
import ResultListTickets from '@/components/filterforms/ResultListTickets.vue';
import ResultPaging from '@/components/filterforms/ResultPaging.vue';
import TabsTicketType, { TabsTicketTypeOption } from '@/components/filterforms/TabsTicketType.vue';
import ButtonPrint from '@/components/interaction/ButtonPrint.vue';
import ButtonReset from '@/components/interaction/ButtonReset.vue';
import ButtonSearch from '@/components/interaction/ButtonSearch.vue';
import LinkBack from '@/components/interaction/LinkBack.vue';
import LoadingContainer from '@/components/structure/LoadingContainer.vue';
import config from '@/config/config';
import { Ticket, TicketStatusEnum } from '@/generated/openapi';
import LoadingMixin from '@/mixins/LoadingMixin';
import { createDefaultTicketSortOptions, createTicketSortOptions, SortOptions } from '@/models/SortOptions';
import { adminService } from '@/services/adminService';
import { now } from '@/utils/dateTimeUtils';
import { LoadingState } from '@/utils/loadingUtils';
import { urlQueryInt, urlQueryString, urlQueryTicketStatusEnum } from '@/utils/navigationUtils';
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'Reservations',
  mixins: [
    LoadingMixin
  ],
  data() {
    return {
      tickets: [] as Ticket[],
      searchType: TicketStatusEnum.Booked,
      searchTerm: '',
      searchFrom: now().toISODate(),
      searchUntil: '',
      sortOptions: createDefaultTicketSortOptions(),
      searchState: LoadingState.success,
      offset: 0,
      totalCountBooked: 0,
      totalCountCancelled: 0
    }
  },
  components: {
    LoadingContainer,
    TabsTicketType,
    ControlSearch,
    ControlDate,
    ButtonReset,
    ButtonPrint,
    ButtonSearch,
    ResultListTickets,
    ResultPaging,
    LinkBack,
  },
  created() {
    this.state = LoadingState.success
    this.historyToState()
    this.doSearch()
  },
  methods: {
    getTabsTicketTypeOptions(): TabsTicketTypeOption[] {
      return [{
          val: TicketStatusEnum.Booked,
          count: this.totalCountBooked
        },
        {
          val: TicketStatusEnum.Cancelled,
          count: this.totalCountCancelled
        }
      ]
    },
    doNewSearch() {
      this.offset = 0
      this.doSearch()
    },
    asStatusFilter(): 'CANCELED_ONLY'|undefined {
      if (this.searchType === TicketStatusEnum.Booked) {
        return undefined;
      } else if (this.searchType === TicketStatusEnum.Cancelled) {
      return 'CANCELED_ONLY';
      }
      return undefined;
    },
    doSearch() {
      this.searchState = LoadingState.loading
      this.stateToHistory();
      Promise.all([
        adminService.findTicketsByDate(this.offset, this.sortOptions, undefined, this.searchTerm || undefined, this.searchFrom || undefined, this.searchUntil || undefined),
        adminService.findTicketsByDate(this.offset, this.sortOptions, 'CANCELED_ONLY', this.searchTerm || undefined, this.searchFrom || undefined, this.searchUntil || undefined)
      ]).then(([bookedResp, cancelledResp]) => {
        this.totalCountBooked = bookedResp.totalCount || 0
        this.totalCountCancelled = cancelledResp.totalCount || 0
        if (this.searchType === TicketStatusEnum.Cancelled) {
          this.tickets = cancelledResp.content || []
        } else {
          this.tickets = bookedResp.content || []
        }
        this.searchState = LoadingState.success
      }).catch(() => {
        this.searchState = LoadingState.error
      })
    },
    stateToHistory() {
      this.$router.push({
        path: this.$route.path,
        query: {
          searchType: this.searchType,
          searchFrom: this.searchFrom,
          searchUntil: this.searchUntil,
          sortColumn: this.sortOptions.column,
          sortOrder: this.sortOptions.sortOrder,
          offset: this.offset,
          searchTerm: this.searchTerm
        }
      })
    },
    historyToState() {
      this.searchType = urlQueryTicketStatusEnum(this.$route, 'searchType')
      this.searchFrom = urlQueryString(this.$route, 'searchFrom') || now().toISODate()
      this.searchUntil = urlQueryString(this.$route, 'searchUntil')
      this.sortOptions = createTicketSortOptions(urlQueryString(this.$route, 'sortColumn'), urlQueryString(this.$route, 'sortOrder') === 'desc')
      this.offset = urlQueryInt(this.$route, 'offset')
      this.searchTerm = urlQueryString(this.$route, 'searchTerm')
    },
    isResettable() {
      return !!(this.searchFrom !== now().toISODate() || this.searchUntil || this.searchTerm)
    },
    resetTermAndDate() {
      this.searchFrom = now().toISODate()
      this.searchUntil = ''
      this.searchTerm = ''
      this.doNewSearch()
    },
    setSearchType(val: TicketStatusEnum) {
      this.searchType = val
      this.doNewSearch()
    },
    activePage() {
      return (this.offset / config.pagingSize) + 1
    },
    maxPage() {
      const totalCountActive = this.searchType === TicketStatusEnum.Booked ? this.totalCountBooked : this.totalCountCancelled
      if (totalCountActive === 0) {
        return 1
      }
      return Math.ceil(totalCountActive / config.pagingSize)
    },
    setActivePage(page: number) {
      this.offset = (page - 1) * config.pagingSize;
      this.doSearch()
    },
    setSortBy(sortOptions: SortOptions) {
      this.sortOptions = sortOptions
      this.doNewSearch()
    }
  }
});
</script>
